/**
 * Contexte global pour la gestion des commandes
 * Hybride: charge depuis le backend quand disponible, sinon localStorage
 * Permet le partage des commandes entre BonCommandeTab et SuiviTab
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import type { Order } from "../components/commandes/commandesTypes";
import {
  fetchPurchaseOrders,
  createPurchaseOrder,
  backendToFrontendOrder,
} from "../services/supplierService";

interface OrdersContextType {
  orders: Order[];
  addOrder: (order: Order) => void;
  updateOrder: (orderId: string, updates: Partial<Order>) => void;
  deleteOrder: (orderId: string) => void;
  getOrdersByStatus: (status: Order["status"]) => Order[];
  getOrdersBySupplier: (supplierId: string) => Order[];
  /** Create a purchase order via the backend API. Falls back to local if backend unreachable. */
  createOrder: (data: {
    supplierId: string;
    expectedDate?: string;
    notes?: string;
    items: { resourceId: string; quantity: number; unitCost: number }[];
  }) => Promise<Order>;
  isFromBackend: boolean;
  refreshOrders: () => Promise<void>;
}

const OrdersContext = createContext<OrdersContextType | undefined>(undefined);

const STORAGE_KEY = "veloce_orders";

export function OrdersProvider({ children }: { children: ReactNode }) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [isFromBackend, setIsFromBackend] = useState(false);

  // Try loading from backend first, fall back to localStorage
  useEffect(() => {
    let cancelled = false;

    async function loadOrders() {
      try {
        const backendPOs = await fetchPurchaseOrders();
        if (!cancelled && backendPOs.length >= 0) {
          const frontendOrders = backendPOs.map(backendToFrontendOrder);
          setOrders(frontendOrders);
          setIsFromBackend(true);
          return;
        }
      } catch {
        // Backend unreachable — fall through to localStorage
      }

      // Fallback: load from localStorage
      if (!cancelled) {
        try {
          const stored = localStorage.getItem(STORAGE_KEY);
          if (stored) {
            const parsedOrders: Order[] = JSON.parse(stored);
            const ordersWithDates = parsedOrders.map((order) => ({
              ...order,
              dateCreation: new Date(order.dateCreation),
              dateEnvoi: order.dateEnvoi ? new Date(order.dateEnvoi) : undefined,
              dateLivraisonEstimee: order.dateLivraisonEstimee
                ? new Date(order.dateLivraisonEstimee)
                : undefined,
              dateReception: order.dateReception ? new Date(order.dateReception) : undefined,
            }));
            setOrders(ordersWithDates);
          }

          // In demo mode, create mock drafts if none exist
          const isDemo = new URLSearchParams(window.location.search).get('demo') === 'true';
          if (isDemo) {
            const draftsStored = localStorage.getItem("veloce_draft_orders");
            if (!draftsStored) {
              import("../components/commandes/commandesMockDrafts").then(({ createMockDraft }) => {
                createMockDraft();
              });
            }
          }
        } catch (error) {
          console.error("Error loading orders from localStorage:", error);
        }
      }
    }

    loadOrders();
    return () => { cancelled = true; };
  }, []);

  // Save to localStorage whenever orders change (only if not from backend)
  useEffect(() => {
    if (!isFromBackend) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(orders));
      } catch (error) {
        console.error("Error saving orders:", error);
      }
    }
  }, [orders, isFromBackend]);

  const refreshOrders = useCallback(async () => {
    try {
      const backendPOs = await fetchPurchaseOrders();
      const frontendOrders = backendPOs.map(backendToFrontendOrder);
      setOrders(frontendOrders);
      setIsFromBackend(true);
    } catch {
      // Keep current orders on refresh failure
    }
  }, []);

  const addOrder = useCallback((order: Order) => {
    setOrders((prev) => [order, ...prev]);
  }, []);

  const updateOrder = useCallback((orderId: string, updates: Partial<Order>) => {
    setOrders((prev) =>
      prev.map((order) => (order.id === orderId ? { ...order, ...updates } : order))
    );
  }, []);

  const deleteOrder = useCallback((orderId: string) => {
    setOrders((prev) => prev.filter((order) => order.id !== orderId));
  }, []);

  const getOrdersByStatus = useCallback((status: Order["status"]) => {
    return orders.filter((order) => order.status === status);
  }, [orders]);

  const getOrdersBySupplier = useCallback((supplierId: string) => {
    return orders.filter((order) => order.supplierId === supplierId);
  }, [orders]);

  /**
   * Create a purchase order via the backend API.
   * Falls back to creating a local order if backend is unreachable.
   */
  const createOrder = useCallback(async (data: {
    supplierId: string;
    expectedDate?: string;
    notes?: string;
    items: { resourceId: string; quantity: number; unitCost: number }[];
  }): Promise<Order> => {
    try {
      const backendPO = await createPurchaseOrder(data);
      const frontendOrder = backendToFrontendOrder(backendPO);
      setOrders((prev) => [frontendOrder, ...prev]);
      setIsFromBackend(true);
      return frontendOrder;
    } catch {
      // Fallback: create local order
      const localOrder: Order = {
        id: `order-${Date.now()}`,
        supplierId: data.supplierId,
        items: data.items.map(item => ({
          productId: item.resourceId,
          productName: item.resourceId, // Best effort
          quantity: item.quantity,
          currentStock: 0,
          unitPrice: item.unitCost,
        })),
        status: "envoyee" as const,
        dateCreation: new Date(),
        dateEnvoi: new Date(),
        dateLivraisonEstimee: data.expectedDate ? new Date(data.expectedDate) : undefined,
      };
      setOrders((prev) => [localOrder, ...prev]);
      return localOrder;
    }
  }, []);

  return (
    <OrdersContext.Provider
      value={{
        orders,
        addOrder,
        updateOrder,
        deleteOrder,
        getOrdersByStatus,
        getOrdersBySupplier,
        createOrder,
        isFromBackend,
        refreshOrders,
      }}
    >
      {children}
    </OrdersContext.Provider>
  );
}

export function useOrders() {
  const context = useContext(OrdersContext);
  if (!context) {
    throw new Error("useOrders must be used within OrdersProvider");
  }
  return context;
}
